import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold.tsx';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandM } from '@sorare/core/src/atoms/typography/index.tsx';
import { fantasy } from '@sorare/core/src/lib/glossary.ts';
import { isType } from '@sorare/core/src/lib/gql';

import type { ContenderPveFooter_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const BarWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: var(--unit);
  height: 50px;
  background: linear-gradient(
      to right,
      var(--color) calc(var(--progress) - 2px),
      var(--c-white) var(--progress),
      transparent calc(var(--progress) + 1px)
    ),
    var(--c-nd-100-opaque);
`;

type Props = {
  onClick?: () => void;
  so5LeaderboardContender: ContenderPveFooter_so5LeaderboardContender;
};
export const ContenderPveFooter = ({
  onClick,
  so5LeaderboardContender,
}: Props) => {
  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const { myTasks, so5League } = so5Leaderboard || {};
  const [task] = myTasks || [];

  if (!isType(task, 'ThresholdsStreakTask')) {
    return null;
  }

  const { thresholds } = task;
  const currentThreshold = thresholds.find(({ current }) => !!current);
  const score = so5Lineup?.so5Rankings.at(0)?.score || 0;
  const progress = (score / (currentThreshold?.score || 1)) * 100;

  return (
    <ButtonBase onClick={onClick}>
      <Vertical>
        <BarWrapper
          style={
            {
              '--color': so5League.color,
              '--progress': `${progress}%`,
            } as CSSProperties
          }
        >
          <BrandM as={Horizontal}>
            <FormattedMessage
              {...fantasy.ordinalPoints}
              values={{
                score,
                sup: content => (
                  <span style={{ color: 'var(--c-nd-600)' }}>{content}</span>
                ),
              }}
            />
          </BrandM>
          <ChevronRightBold color="var(--c-nd-600)" />
        </BarWrapper>
      </Vertical>
    </ButtonBase>
  );
};

ContenderPveFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderPveFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Rankings {
          id
          score
        }
      }
      so5Leaderboard {
        slug
        id
        so5League {
          slug
          color(variant: PRIMARY)
        }
        myTasks {
          id
          ... on ThresholdsStreakTask {
            id
            thresholds {
              current
              score
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<ContenderPveFooter_so5LeaderboardContender>,
};
